import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';
import { 
  Home, 
  ClipboardList, 
  Users, 
  Settings, 
  LogOut,
  Building,
  BarChart3
} from 'lucide-react';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isCellWise = user?.role === 'ECSBC-Professional' || user?.role === 'Nodal Officer SDA';

  const navigation = [
    { name: 'BEE Dashboard', href: '/dashboard', icon: Home, roles: ['BEE-Admin', 'Super-Admin', 'ECSBC-Professional', 'Nodal Officer SDA'] },
    { name: 'RACI Matrix', href: '/raci-matrix', icon: ClipboardList, roles: ['BEE-Admin', 'Super-Admin', 'ECSBC-Professional', 'Nodal Officer SDA'], hideForCellWise: true },
    { name: 'DG dashboard', href: '/dg-dashboard', icon: BarChart3, roles: ['BEE-Admin', 'Super-Admin', 'ECSBC-Professional', 'Nodal Officer SDA'], hideForCellWise: true },
    { name: 'User Management', href: '/user-management', icon: Users, roles: ['Super-Admin'] },
    { name: 'Master Data', href: '/master-data', icon: Settings, roles: ['Super-Admin'] },
  ];

  const filteredNavigation = navigation.filter(item => {
    if (!item.roles.includes(user?.role)) return false;
    if (isCellWise && item.hideForCellWise) return false;
    return true;
  });

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700">
            <div className="flex items-center space-x-2">
              <Building className="h-7 w-7 text-white" />
              <span className="text-lg font-bold text-white">ECSBC Cell Tracker</span>
            </div>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-white">
            <div className="flex items-center space-x-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold text-sm">
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 truncate">{user?.fullName || user?.username}</div>
                <div className="text-xs text-gray-500 capitalize truncate">{user?.role?.replace('-', ' ')}</div>
              </div>
            </div>
            {user?.cellNo && (
              <div className="space-y-1 pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Cell</span>
                  <span className="font-medium text-gray-700">{user.cellNo}</span>
                </div>
                {user?.cellState && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">State</span>
                    <span className="font-medium text-gray-700">{user.cellState}</span>
                  </div>
                )}
                {user?.cellEstablishmentDate && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Cell Establishment Date</span>
                    <span className="font-medium text-gray-700">
                      {new Date(user.cellEstablishmentDate).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive(item.href)
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-primary-600'
                  }`}
                >
                  <Icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-white' : 'text-gray-500'}`} />
                  {item.href === '/dashboard' && isCellWise ? 'Dashboard' : item.name}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="px-3 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={logout}
              className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg hover:bg-red-50 hover:text-red-600 transition-all duration-200"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 flex flex-col min-h-screen">
        <main className="flex-1 min-w-0 max-w-full p-6 pb-0 overflow-x-hidden">
          <Outlet />
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default Layout;
